import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout/Layout';
import { authAPI } from '../services/api';
import Button from '../components/UI/Button';
import { useAuth } from '../contexts/AuthContext';

interface UserProfile {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
  joinDate: string;
  avatar?: string;
}

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  isActive: boolean;
  createdAt: string;
  lastLogin?: string;
}

const Profile: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState<'personal' | 'security' | 'users'>('personal');

  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  // Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  
  // Profile update password confirmation
  const [profileUpdatePassword, setProfileUpdatePassword] = useState('');

  // User management state
  const [users, setUsers] = useState<User[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  
  // Create user form state
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserFirstName, setNewUserFirstName] = useState('');
  const [newUserLastName, setNewUserLastName] = useState('');
  const [newUserRole, setNewUserRole] = useState('admin');
  const [newUserTemporaryPassword, setNewUserTemporaryPassword] = useState('');

  // Edit user state
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [editUserEmail, setEditUserEmail] = useState('');
  const [editUserFirstName, setEditUserFirstName] = useState('');
  const [editUserLastName, setEditUserLastName] = useState('');
  const [editUserRole, setEditUserRole] = useState('admin');
  const [editUserTemporaryPassword, setEditUserTemporaryPassword] = useState('');

  // Delete user state
  const [isDeletingUser, setIsDeletingUser] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);

  const isAdmin = currentUser?.role === 'admin';


  useEffect(() => {
    // Load user profile from API
    const loadProfile = async () => {
      try {
        setIsLoading(true);
        
        // Load profile from API
        const profileResponse = await authAPI.getProfile();
        if (profileResponse.success) {
          const userProfile = profileResponse.data;
          setProfile(userProfile);
          setFirstName(userProfile.firstName);
          setLastName(userProfile.lastName);
          setEmail(userProfile.email);
          setPhone(userProfile.phone || '');

        }
      } catch (error) {
        console.error('Error loading profile:', error);
        // Fallback to auth context data
        const userData = localStorage.getItem('shelter-user');
        if (userData) {
          const user = JSON.parse(userData);
          const userProfile: UserProfile = {
            id: user.id,
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            phone: '',
            role: user.role,
            joinDate: user.createdAt,
            avatar: ''
          };
          setProfile(userProfile);
          setFirstName(userProfile.firstName);
          setLastName(userProfile.lastName);
          setEmail(userProfile.email);
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, []);

  // Load users for admin
  const loadUsers = async () => {
    if (!isAdmin) return;
    
    try {
      setIsLoadingUsers(true);
      const response = await authAPI.getUsers();
      if (response.success) {
        setUsers(response.data.users);
      }
    } catch (error) {
      console.error('Error loading users:', error);
      alert('Eroare la încărcarea utilizatorilor');
    } finally {
      setIsLoadingUsers(false);
    }
  };

  // Load users when switching to users tab
  useEffect(() => {
    if (activeTab === 'users' && isAdmin) {
      loadUsers();
    }
  }, [activeTab, isAdmin]);

  // Create new user
  const handleCreateUser = async () => {
    if (!newUserEmail || !newUserFirstName || !newUserLastName || !newUserTemporaryPassword) {
      alert('Toate câmpurile sunt obligatorii');
      return;
    }

    if (newUserTemporaryPassword.length < 6) {
      alert('Parola temporară trebuie să conțină cel puțin 6 caractere');
      return;
    }

    try {
      setIsCreatingUser(true);
      const userData = {
        email: newUserEmail,
        firstName: newUserFirstName,
        lastName: newUserLastName,
        role: newUserRole,
        temporaryPassword: newUserTemporaryPassword
      };

      const response = await authAPI.createUser(userData);

      if (response.success) {
        alert('Utilizatorul a fost creat cu succes. Comunicați parola temporară printr-un canal sigur.');
        setShowCreateForm(false);
        setNewUserEmail('');
        setNewUserFirstName('');
        setNewUserLastName('');
        setNewUserRole('admin');
        setNewUserTemporaryPassword('');
        loadUsers(); // Refresh users list
      }
    } catch (error) {
      console.error('Error creating user:', error);
      alert('Eroare la crearea utilizatorului');
    } finally {
      setIsCreatingUser(false);
    }
  };

  // Start editing user
  const handleStartEditUser = (user: User) => {
    setEditingUser(user);
    setEditUserEmail(user.email);
    setEditUserFirstName(user.firstName);
    setEditUserLastName(user.lastName);
    setEditUserRole(user.role);
    setEditUserTemporaryPassword('');
  };

  // Update user
  const handleUpdateUser = async () => {
    if (!editingUser || !editUserEmail || !editUserFirstName || !editUserLastName) {
      alert('Toate câmpurile sunt obligatorii');
      return;
    }

    try {
      setIsEditingUser(true);
      const updateData: any = {
        email: editUserEmail,
        firstName: editUserFirstName,
        lastName: editUserLastName,
        role: editUserRole
      };

      if (editUserTemporaryPassword) {
        updateData.temporaryPassword = editUserTemporaryPassword;
      }

      const response = await authAPI.updateUser(editingUser.id, updateData);

      if (response.success) {
        alert(editUserTemporaryPassword 
          ? `Utilizatorul a fost actualizat cu succes! Noua parolă temporară este "${editUserTemporaryPassword}"`
          : 'Utilizatorul a fost actualizat cu succes!'
        );
        setEditingUser(null);
        setEditUserEmail('');
        setEditUserFirstName('');
        setEditUserLastName('');
        setEditUserRole('admin');
        setEditUserTemporaryPassword('');
        loadUsers(); // Refresh users list
      }
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Eroare la actualizarea utilizatorului');
    } finally {
      setIsEditingUser(false);
    }
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingUser(null);
    setEditUserEmail('');
    setEditUserFirstName('');
    setEditUserLastName('');
    setEditUserRole('admin');
    setEditUserTemporaryPassword('');
  };

  const handleCancelProfileEdit = () => {
    if (!profile) return;
    
    setFirstName(profile.firstName);
    setLastName(profile.lastName);
    setEmail(profile.email);
    setPhone(profile.phone);
    setProfileUpdatePassword('');
    setIsEditing(false);
  };

  // Toggle user status
  const handleToggleUserStatus = async (userId: string, currentStatus: boolean) => {
    try {
      const response = await authAPI.updateUserStatus(userId, !currentStatus);
      if (response.success) {
        alert(`Statusul utilizatorului a fost ${!currentStatus ? 'activat' : 'dezactivat'}`);
        loadUsers(); // Refresh users list
      }
    } catch (error) {
      console.error('Error updating user status:', error);
      alert('Eroare la actualizarea statusului utilizatorului');
    }
  };

  // Delete user
  const handleDeleteUser = async (user: User) => {
    if (user.id === currentUser?.id) {
      alert('Nu îți poți șterge propriul cont!');
      return;
    }

    const confirmMessage = `Ești sigur că vrei să ștergi utilizatorul "${user.firstName} ${user.lastName}"?\n\nAceastă acțiune nu poate fi anulată.`;
    
    if (!confirm(confirmMessage)) {
      return;
    }

    try {
      setIsDeletingUser(true);
      setUserToDelete(user);
      
      const response = await authAPI.deleteUser(user.id);
      if (response.success) {
        alert(`Utilizatorul "${user.firstName} ${user.lastName}" a fost șters cu succes.`);
        loadUsers(); // Refresh users list
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Eroare la ștergerea utilizatorului. Verifică dacă nu este ultimul administrator.');
    } finally {
      setIsDeletingUser(false);
      setUserToDelete(null);
    }
  };

  const handleSaveProfile = async () => {
    if (!profile) return;

    // Check if password is required for email change
    if (email !== profile.email && !profileUpdatePassword) {
      alert('Te rugăm să introduci parola curentă pentru a confirma modificarea adresei de email.');
      return;
    }

    try {
      const profileData = {
        firstName,
        lastName,
        email,
        phone,
        ...(email !== profile.email && { currentPassword: profileUpdatePassword })
      };

      await authAPI.updateProfile(profileData);
      
      const updatedProfile: UserProfile = {
        ...profile,
        ...profileData
      };

      setProfile(updatedProfile);
      setIsEditing(false);
      setProfileUpdatePassword('');
      alert('Profilul a fost actualizat cu succes!');
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Eroare la actualizarea profilului. Verifică parola introdusă.');
    }
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      alert('Parolele nu se potrivesc!');
      return;
    }
    if (newPassword.length < 6) {
      alert('Parola trebuie să aibă cel puțin 6 caractere!');
      return;
    }
    if (currentPassword === newPassword) {
      alert('Parola nouă trebuie să fie diferită de cea curentă!');
      return;
    }

    setIsChangingPassword(true);

    try {
      const response = await authAPI.changePassword({
        currentPassword,
        newPassword
      });

      if (response.success) {
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        alert('Parola a fost schimbată cu succes!');
      } else {
        alert(response.error || 'Eroare la schimbarea parolei');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      alert('Eroare la schimbarea parolei. Verifică parola curentă.');
    } finally {
      setIsChangingPassword(false);
    }
  };



  if (isLoading || !profile) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Profil utilizator</h1>
            <p className="text-gray-600">Gestionează informațiile contului</p>
          </div>
          {activeTab === 'personal' && (
            <div className="flex space-x-2">
              {isEditing ? (
                <>
                  <Button variant="outline" onClick={handleCancelProfileEdit}>
                    Anulează
                  </Button>
                  <Button variant="primary" onClick={handleSaveProfile}>
                    Salvează
                  </Button>
                </>
              ) : (
                <Button variant="primary" onClick={() => setIsEditing(true)}>
                  Editează profil
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Profile Card */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab('personal')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'personal'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Informații personale
              </button>
              <button
                onClick={() => setActiveTab('security')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'security'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Securitate
              </button>
              {isAdmin && (
                <button
                  onClick={() => setActiveTab('users')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'users'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Gestionare utilizatori
                </button>
              )}
            </nav>
          </div>

          <div className="p-6">
            {/* Personal Information Tab */}
            {activeTab === 'personal' && (
              <div className="space-y-6">
                {/* Avatar Section */}
                <div className="flex items-center space-x-6">
                  <div className="flex-shrink-0">
                    {profile.avatar ? (
                      <img
                        src={profile.avatar}
                        alt="Avatar"
                        className="h-20 w-20 rounded-full object-cover"
                      />
                    ) : (
                      <div className="h-20 w-20 rounded-full bg-gray-300 flex items-center justify-center">
                        <svg className="h-12 w-12 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      {firstName} {lastName}
                    </h3>
                    <p className="text-sm text-gray-500">{profile.role}</p>
                    <p className="text-sm text-gray-500">Membru din {new Date(profile.joinDate).toLocaleDateString('ro-RO')}</p>
                  </div>
                </div>

                {/* Form Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Prenume
                    </label>
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nume
                    </label>
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Telefon
                    </label>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-gray-50"
                    />
                  </div>
                </div>

                {/* Password confirmation for email change */}
                {isEditing && profile && email !== profile.email && (
                  <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                    <div className="flex items-center mb-2">
                      <svg className="w-5 h-5 text-yellow-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <p className="text-sm font-medium text-yellow-800">
                        Confirmare necesară pentru modificarea adresei de email
                      </p>
                    </div>
                    <div className="max-w-md">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Parola curentă
                      </label>
                      <input
                        type="password"
                        value={profileUpdatePassword}
                        onChange={(e) => setProfileUpdatePassword(e.target.value)}
                        placeholder="Introdu parola curentă pentru confirmare"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                  </div>
                )}

              </div>
            )}

            {/* Security Tab */}
            {activeTab === 'security' && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900">
                  Schimbă parola
                </h3>
                <div className="max-w-md space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Parola curentă
                    </label>
                    <input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Parola nouă
                    </label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Confirmă parola nouă
                    </label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <Button 
                    variant="primary" 
                    onClick={handleChangePassword}
                    disabled={!currentPassword || !newPassword || !confirmPassword || isChangingPassword}
                  >
                    {isChangingPassword ? 'Se schimbă parola...' : 'Schimbă parola'}
                  </Button>
                </div>
              </div>
            )}

            {/* Users Tab */}
            {activeTab === 'users' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium text-gray-900">
                    Gestionare utilizatori
                  </h3>
                  <Button variant="primary" onClick={() => setShowCreateForm(true)}>
                    Creează utilizator nou
                  </Button>
                </div>
                
                {isLoadingUsers ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {users.length === 0 ? (
                      <div className="col-span-2 text-center py-8 text-gray-500">
                        Nu există utilizatori.
                      </div>
                    ) : (
                      users.map(user => (
                        <div key={user.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-2">
                                <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center">
                                  <span className="text-primary font-medium">
                                    {user.firstName[0]}{user.lastName[0]}
                                  </span>
                                </div>
                                <div>
                                  <h4 className="font-medium text-gray-900">{user.firstName} {user.lastName}</h4>
                                  <p className="text-sm text-gray-500">{user.email}</p>
                                </div>
                              </div>
                              
                              <div className="space-y-1 text-sm">
                                <div className="flex items-center space-x-2">
                                  <span className="text-gray-500">Rol:</span>
                                  <span className="font-medium capitalize">{user.role}</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <span className="text-gray-500">Status:</span>
                                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                    user.isActive 
                                      ? 'bg-green-100 text-green-800' 
                                      : 'bg-red-100 text-red-800'
                                  }`}>
                                    {user.isActive ? 'Activ' : 'Inactiv'}
                                  </span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <span className="text-gray-500">Membru din:</span>
                                  <span>{new Date(user.createdAt).toLocaleDateString('ro-RO')}</span>
                                </div>
                                {user.lastLogin && (
                                  <div className="flex items-center space-x-2">
                                    <span className="text-gray-500">Ultima autentificare:</span>
                                    <span>{new Date(user.lastLogin).toLocaleDateString('ro-RO')}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            <div className="flex flex-col space-y-2 ml-4">
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleStartEditUser(user)}
                              >
                                Editează
                              </Button>
                              {user.id !== currentUser?.id && (
                                <Button 
                                  variant={user.isActive ? 'outline' : 'primary'} 
                                  size="sm"
                                  onClick={() => handleToggleUserStatus(user.id, user.isActive)}
                                >
                                  {user.isActive ? 'Dezactivează' : 'Activează'}
                                </Button>
                              )}
                              {user.id !== currentUser?.id && (
                                <button
                                  className={`px-2 py-1 text-xs font-medium rounded border transition-colors ${
                                    isDeletingUser && userToDelete?.id === user.id
                                      ? 'bg-red-100 text-red-700 border-red-300 cursor-not-allowed'
                                      : 'bg-white text-red-600 border-red-300 hover:bg-red-50 hover:text-red-700'
                                  }`}
                                  onClick={() => handleDeleteUser(user)}
                                  disabled={isDeletingUser && userToDelete?.id === user.id}
                                >
                                  {isDeletingUser && userToDelete?.id === user.id ? 'Ștergând...' : 'Șterge'}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}

            {showCreateForm && activeTab === 'users' && (
              <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Creează utilizator nou</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={newUserEmail}
                      onChange={(e) => setNewUserEmail(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Prenume
                    </label>
                    <input
                      type="text"
                      value={newUserFirstName}
                      onChange={(e) => setNewUserFirstName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nume
                    </label>
                    <input
                      type="text"
                      value={newUserLastName}
                      onChange={(e) => setNewUserLastName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Rol
                    </label>
                    <select
                      value={newUserRole}
                      onChange={(e) => setNewUserRole(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="admin">Admin</option>
                      <option value="user">Utilizator</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Parolă temporară
                    </label>
                    <input
                      type="password"
                      value={newUserTemporaryPassword}
                      onChange={(e) => setNewUserTemporaryPassword(e.target.value)}
                      placeholder="Minimum 6 caractere"
                      minLength={6}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Alegeți o parolă temporară și comunicați-o utilizatorului printr-un canal sigur.
                    </p>
                  </div>
                </div>
                <div className="flex space-x-2 mt-6">
                  <Button variant="primary" onClick={handleCreateUser} disabled={isCreatingUser}>
                    {isCreatingUser ? 'Se creează utilizator...' : 'Creează utilizator'}
                  </Button>
                  <Button variant="outline" onClick={() => setShowCreateForm(false)}>
                    Anulează
                  </Button>
                </div>
              </div>
            )}

            {/* Edit User Modal */}
            {editingUser && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Editează utilizator: {editingUser.firstName} {editingUser.lastName}
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        value={editUserEmail}
                        onChange={(e) => setEditUserEmail(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Prenume
                      </label>
                      <input
                        type="text"
                        value={editUserFirstName}
                        onChange={(e) => setEditUserFirstName(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nume
                      </label>
                      <input
                        type="text"
                        value={editUserLastName}
                        onChange={(e) => setEditUserLastName(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Rol
                      </label>
                      <select
                        value={editUserRole}
                        onChange={(e) => setEditUserRole(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <option value="admin">Admin</option>
                        <option value="user">Utilizator</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Parolă temporară nouă (opțional)
                      </label>
                      <input
                        type="password"
                        value={editUserTemporaryPassword}
                        onChange={(e) => setEditUserTemporaryPassword(e.target.value)}
                        placeholder="Lasă gol pentru a păstra parola actuală"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Completează doar dacă vrei să resetezi parola utilizatorului.
                      </p>
                    </div>
                  </div>
                  <div className="flex space-x-2 mt-6">
                    <Button variant="primary" onClick={handleUpdateUser} disabled={isEditingUser}>
                      {isEditingUser ? 'Se actualizează...' : 'Actualizează'}
                    </Button>
                    <Button variant="outline" onClick={handleCancelEdit}>
                      Anulează
                    </Button>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Profile;
